import React, { useContext, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import UploadFile from '@mui/icons-material/UploadFile';
import Visibility from '@mui/icons-material/Visibility';
import CloudUpload from '@mui/icons-material/CloudUpload';
import { toast } from 'react-toastify';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import http from '../../services/http';

/**
 * Importação da base de clientes por planilha (Fase 3b).
 *
 * A tela é deliberadamente de DOIS PASSOS: conferir e só então gravar. Trocar a
 * base de clientes inteira num clique é o tipo de coisa que a pessoa faz uma
 * vez e passa o resto do dia desfazendo.
 *
 * A prévia é confiável porque o servidor a produz pelo MESMO caminho da
 * gravação, com um parâmetro desligado -- e não por uma simulação escrita
 * à parte, que acabaria divergindo do que a gravação faz de verdade.
 */

/** As colunas que o importador entende. Só as duas primeiras são obrigatórias. */
const COLUNAS = [
    { titulo: 'Identificação', obrigatoria: true },
    { titulo: 'Razão Social', obrigatoria: true },
    { titulo: 'Nome Fantasia', obrigatoria: false },
    { titulo: 'CNPJ', obrigatoria: false },
    { titulo: 'E-mail', obrigatoria: false },
    { titulo: 'Telefone', obrigatoria: false },
    { titulo: 'Cidade', obrigatoria: false },
    { titulo: 'UF', obrigatoria: false },
];

const ClientImportPage = () => {
    const { theme } = useContext(ThemeContext);
    const inputRef = useRef(null);

    const [arquivo, setArquivo] = useState(null);
    const [previa, setPrevia] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [conferindo, setConferindo] = useState(false);
    const [importando, setImportando] = useState(false);

    const escolherArquivo = (e) => {
        setArquivo(e.target.files?.[0] || null);
        // A prévia vale para o arquivo que a gerou. Trocar o arquivo e manter a
        // prévia na tela seria oferecer um "Importar" que grava outra coisa.
        setPrevia(null);
        setResultado(null);
    };

    const enviar = async (caminho) => {
        const form = new FormData();
        form.append('planilha', arquivo);

        return http.post(caminho, form, { timeout: 300000 });
    };

    const conferir = async () => {
        if (!arquivo) {
            toast.warning('Selecione a planilha primeiro.');
            return;
        }

        setConferindo(true);
        setResultado(null);

        try {
            const res = await enviar('/clients/import/preview');
            setPrevia(res.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Não foi possível ler a planilha.');
        } finally {
            setConferindo(false);
        }
    };

    const importar = async () => {
        setImportando(true);

        try {
            const res = await enviar('/clients/import');
            // Só `resultado`: ele tem precedência sobre a prévia logo abaixo,
            // então zerar a prévia aqui não mudaria nada na tela. Um teste de
            // mutação mostrou isso -- a linha existia e não fazia diferença.
            setResultado(res.data);
            toast.success('Importação concluída.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Não foi possível importar a planilha.');
        } finally {
            setImportando(false);
        }
    };

    const relatorio = resultado || previa;

    const totais = relatorio
        ? [
            { rotulo: 'Novas', valor: relatorio.criadas, cor: 'success' },
            { rotulo: 'Atualizadas', valor: relatorio.atualizadas, cor: 'info' },
            { rotulo: 'Sem mudança', valor: relatorio.inalteradas, cor: 'default' },
            { rotulo: 'Recusadas', valor: relatorio.recusadas, cor: 'error' },
        ]
        : [];

    const recusadas = relatorio?.exemplos?.recusadas || [];
    const novas = relatorio?.exemplos?.criadas || [];
    const atualizadas = relatorio?.exemplos?.atualizadas || [];

    /**
     * Uma tabela de linhas do relatório.
     *
     * Conferir não é olhar totais, é RECONHECER as empresas: se aparecer um
     * nome que não devia estar ali, a pessoa cancela antes de gravar. Um
     * "5 novas" sozinho não permite isso.
     */
    const tabela = (titulo, linhas, total, colunas) => {
        if (linhas.length === 0) return null;

        return (
            <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {titulo}
                    {total > linhas.length ? ` (as ${linhas.length} primeiras de ${total})` : ''}
                </Typography>
                <TableContainer sx={{ maxHeight: 300, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 70 }}>Linha</TableCell>
                                {colunas.map((coluna) => (
                                    <TableCell key={coluna.chave}>{coluna.titulo}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {linhas.map((linha) => (
                                <TableRow key={`${titulo}-${linha.linha}`}>
                                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                                        {linha.linha}
                                    </TableCell>
                                    {colunas.map((coluna) => (
                                        <TableCell key={coluna.chave}>
                                            {coluna.formatar ? coluna.formatar(linha) : linha[coluna.chave]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Importar clientes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Envie uma planilha (.xlsx) com a sua base de clientes. Nada é gravado antes de você conferir.
            </Typography>

            {/* ------------------------------------------------ as colunas */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Colunas da planilha
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    A ordem não importa — as colunas são reconhecidas pelo título. Acentos, maiúsculas e
                    pontuação são ignorados, então <em>E-mail</em> e <em>email</em> são a mesma coluna.
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {COLUNAS.map((coluna) => (
                        <Chip
                            key={coluna.titulo}
                            size="small"
                            label={coluna.obrigatoria ? `${coluna.titulo} *` : coluna.titulo}
                            color={coluna.obrigatoria ? 'primary' : 'default'}
                            variant={coluna.obrigatoria ? 'filled' : 'outlined'}
                        />
                    ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    * obrigatórias. A identificação pode ser CNPJ, CPF ou um código próprio (aceita letras).
                    Linha que já existe é atualizada, e coluna vazia não apaga o que já estava gravado.
                </Typography>
            </Paper>

            {/* ------------------------------------------------- o arquivo */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <input
                        type="file"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        ref={inputRef}
                        style={{ display: 'none' }}
                        onChange={escolherArquivo}
                    />

                    <Button
                        variant="outlined"
                        startIcon={<UploadFile />}
                        onClick={() => inputRef.current?.click()}
                        sx={{ textTransform: 'none' }}
                    >
                        Selecionar planilha
                    </Button>

                    <Typography variant="body2" color={arquivo ? 'text.primary' : 'text.secondary'}>
                        {arquivo ? arquivo.name : 'Nenhum arquivo selecionado'}
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={conferindo ? <CircularProgress size={16} color="inherit" /> : <Visibility />}
                        onClick={conferir}
                        disabled={!arquivo || conferindo || importando}
                        sx={{ textTransform: 'none', ml: 'auto' }}
                    >
                        Conferir
                    </Button>
                </Box>
            </Paper>

            {/* ------------------------------------------------- o relatório */}
            {relatorio && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {relatorio.aplicado ? 'Importação concluída' : 'Prévia'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {relatorio.total} linha(s) lida(s)
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {totais.map((t) => (
                            <Chip
                                key={t.rotulo}
                                label={`${t.rotulo}: ${t.valor}`}
                                color={t.valor > 0 ? t.cor : 'default'}
                                variant={t.valor > 0 ? 'filled' : 'outlined'}
                                size="small"
                            />
                        ))}
                    </Box>

                    {!relatorio.aplicado && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Nada foi gravado ainda. Confira os números acima e, se estiverem certos, clique em
                            “Importar”.
                        </Alert>
                    )}

                    {tabela(
                        relatorio.aplicado ? 'Empresas criadas' : 'Serão criadas',
                        novas,
                        relatorio.criadas,
                        [{ chave: 'identificacao', titulo: 'Identificação' }, { chave: 'nome', titulo: 'Razão social' }]
                    )}

                    {tabela(
                        relatorio.aplicado ? 'Empresas atualizadas' : 'Serão atualizadas',
                        atualizadas,
                        relatorio.atualizadas,
                        [
                            { chave: 'identificacao', titulo: 'Identificação' },
                            { chave: 'nome', titulo: 'Razão social' },
                            {
                                chave: 'campos',
                                titulo: 'O que muda',
                                // Dizer QUE campo muda é o que evita gravar sem
                                // perceber que o telefone vai ser trocado.
                                formatar: (linha) => (linha.campos || []).join(', ') || '—',
                            },
                        ]
                    )}

                    {recusadas.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Linhas recusadas
                                {relatorio.recusadas > recusadas.length
                                    ? ` (as ${recusadas.length} primeiras de ${relatorio.recusadas})`
                                    : ''}
                            </Typography>
                            <TableContainer sx={{ maxHeight: 320, mb: 2, overflowX: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: 90 }}>Linha</TableCell>
                                            <TableCell>Motivo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {recusadas.map((linha) => (
                                            <TableRow key={linha.linha}>
                                                <TableCell sx={{ color: theme.palette.text.secondary }}>
                                                    {linha.linha}
                                                </TableCell>
                                                <TableCell>{linha.motivo}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                O número é o da linha na planilha, como o Excel mostra. As demais linhas entram
                                normalmente — uma linha recusada não impede as outras.
                            </Typography>
                        </>
                    )}

                    {!relatorio.aplicado && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={importando ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                            onClick={importar}
                            disabled={importando || relatorio.criadas + relatorio.atualizadas === 0}
                            sx={{ textTransform: 'none' }}
                        >
                            Importar {relatorio.criadas + relatorio.atualizadas} cliente(s)
                        </Button>
                    )}

                    {!relatorio.aplicado && relatorio.criadas + relatorio.atualizadas === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Não há nada a gravar: nenhuma linha desta planilha cria ou altera cliente.
                        </Typography>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default ClientImportPage;
